import RGBA from './rgbaUtils';

interface Lab {
  l: number;
  a: number;
  b: number;
}

function toLinear(channel: number): number {
  const value = channel / 255;
  return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
}

function labF(t: number): number {
  return t > 216 / 24389 ? Math.cbrt(t) : ((24389 / 27) * t + 16) / 116;
}

// sRGB to CIELAB under the sRGB white point (D65).
function rgbToLab({ r, g, b }: RGBA): Lab {
  const red = toLinear(r);
  const green = toLinear(g);
  const blue = toLinear(b);
  const fx = labF((0.4124564 * red + 0.3575761 * green + 0.1804375 * blue) / 0.95047);
  const fy = labF(0.2126729 * red + 0.7151522 * green + 0.072175 * blue);
  const fz = labF((0.0193339 * red + 0.119192 * green + 0.9503041 * blue) / 1.08883);
  return { l: 116 * fy - 16, a: 500 * (fx - fy), b: 200 * (fy - fz) };
}

function toRadians(degrees: number): number {
  return (degrees * Math.PI) / 180;
}

function hueDegrees(b: number, aPrime: number): number {
  if (b === 0 && aPrime === 0) {
    return 0;
  }
  const degrees = (Math.atan2(b, aPrime) * 180) / Math.PI;
  return degrees < 0 ? degrees + 360 : degrees;
}

/**
 * How different two sRGB colours look, as CIEDE2000 (Sharma, Wu and Dalal, 2005).
 * Under 2 is hard to see side by side; 5 and up is obvious.
 */
export default function deltaE2000(color1: RGBA, color2: RGBA): number {
  const lab1 = rgbToLab(color1);
  const lab2 = rgbToLab(color2);

  const chromaMean7 = ((Math.hypot(lab1.a, lab1.b) + Math.hypot(lab2.a, lab2.b)) / 2) ** 7;
  const g = 0.5 * (1 - Math.sqrt(chromaMean7 / (chromaMean7 + 25 ** 7)));
  const a1 = (1 + g) * lab1.a;
  const a2 = (1 + g) * lab2.a;
  const c1 = Math.hypot(a1, lab1.b);
  const c2 = Math.hypot(a2, lab2.b);
  const h1 = hueDegrees(lab1.b, a1);
  const h2 = hueDegrees(lab2.b, a2);
  const hasHue = c1 * c2 !== 0;

  let hueDifference = 0;
  if (hasHue) {
    hueDifference = h2 - h1;
    if (hueDifference > 180) {
      hueDifference -= 360;
    } else if (hueDifference < -180) {
      hueDifference += 360;
    }
  }

  let hueMean = h1 + h2;
  if (hasHue) {
    if (Math.abs(h1 - h2) <= 180) {
      hueMean = (h1 + h2) / 2;
    } else {
      hueMean = (h1 + h2 + (h1 + h2 < 360 ? 360 : -360)) / 2;
    }
  }

  const deltaL = lab2.l - lab1.l;
  const deltaC = c2 - c1;
  const deltaH = 2 * Math.sqrt(c1 * c2) * Math.sin(toRadians(hueDifference / 2));
  const lightnessMean = (lab1.l + lab2.l) / 2;
  const chromaMean = (c1 + c2) / 2;
  const t =
    1 -
    0.17 * Math.cos(toRadians(hueMean - 30)) +
    0.24 * Math.cos(toRadians(2 * hueMean)) +
    0.32 * Math.cos(toRadians(3 * hueMean + 6)) -
    0.2 * Math.cos(toRadians(4 * hueMean - 63));
  const rotation = 30 * Math.exp(-(((hueMean - 275) / 25) ** 2));
  const chromaMeanPrime7 = chromaMean ** 7;
  const rotationTerm =
    -2 * Math.sqrt(chromaMeanPrime7 / (chromaMeanPrime7 + 25 ** 7)) * Math.sin(toRadians(2 * rotation));
  const lightnessScale = 1 + (0.015 * (lightnessMean - 50) ** 2) / Math.sqrt(20 + (lightnessMean - 50) ** 2);
  const chromaScale = 1 + 0.045 * chromaMean;
  const hueScale = 1 + 0.015 * chromaMean * t;

  return Math.sqrt(
    (deltaL / lightnessScale) ** 2 +
      (deltaC / chromaScale) ** 2 +
      (deltaH / hueScale) ** 2 +
      rotationTerm * (deltaC / chromaScale) * (deltaH / hueScale),
  );
}
